<script lang="ts">
  import { toPersianDigits } from '@clinic/money-date';
  import { untrack } from 'svelte';
  import { fa } from '../i18n/fa';
  import { location, navigate } from '../router/router.svelte';
  import { diag, diagText, maskPath, record, startDiag, stopDiag } from './diag.svelte';

  /**
   * The bar of the touch recorder (shared/diag/diag.svelte.ts): `?diag=1` on any page starts it (the
   * command palette opens that), each page change is a line, and the bar copies the record or stops
   * it. It is a manual popover so it stays above the page and a panel opened before it.
   */
  let bar = $state<HTMLDivElement>();
  let copied = $state<'ok' | 'failed' | null>(null);
  let fallback = $state(false);

  $effect(() => {
    const search = location.search;
    const path = location.pathname;
    untrack(() => {
      // A copy read once, outside any reactive state.
      // eslint-disable-next-line svelte/prefer-svelte-reactivity
      const params = new URLSearchParams(search);
      if (params.get('diag') === '1') {
        params.delete('diag');
        const rest = params.toString();
        navigate(`${path}${rest ? `?${rest}` : ''}`, { replace: true });
        startDiag();
        return;
      }
      record('route', maskPath(path + search));
    });
  });

  $effect(() => {
    const el = bar;
    if (!el || typeof el.showPopover !== 'function') return;
    if (diag.on && !el.matches(':popover-open')) el.showPopover();
  });

  async function copy() {
    const text = diagText();
    try {
      await navigator.clipboard.writeText(text);
      copied = 'ok';
      fallback = false;
    } catch {
      copied = 'failed';
      fallback = true;
    }
  }
</script>

{#if diag.on}
  <div
    bind:this={bar}
    popover="manual"
    role="region"
    aria-label={fa.diag.title}
    data-testid="diag-bar"
    class="fixed inset-x-2 top-auto m-0 rounded-lg border border-border-strong bg-surface p-2 text-sm text-text shadow-2 sm:inset-x-auto sm:end-4 sm:w-80"
    style="bottom: calc(env(safe-area-inset-bottom) + 5rem);"
  >
    <div class="flex items-center justify-between gap-2">
      <p class="min-w-0 truncate">{fa.diag.recording(toPersianDigits(diag.count))}</p>
      <div class="flex shrink-0 gap-1">
        <button type="button" onclick={() => void copy()} class="min-h-9 rounded-md bg-accent-solid px-3 font-medium text-on-accent">{fa.diag.copy}</button>
        <button type="button" onclick={stopDiag} class="min-h-9 rounded-md px-3 text-text-2 hover:bg-surface-3">{fa.diag.stop}</button>
      </div>
    </div>
    <p aria-live="polite" class="text-xs text-text-3 empty:hidden">{#if copied === 'ok'}{fa.diag.copied}{:else if copied === 'failed'}{fa.diag.copyFailed}{/if}</p>
    {#if fallback}
      <textarea readonly dir="ltr" class="mt-2 h-32 w-full rounded-md border border-border bg-surface-2 p-2 font-mono text-xs" aria-label={fa.diag.title}
        >{diagText()}</textarea
      >
    {/if}
  </div>
{/if}
